import { Engagement, Client, ArchivedFile, EngagementFormData } from "@/types/engagement";

const ENGAGEMENTS_STORAGE_KEY = "juggernaut_engagements";
const CLIENTS_STORAGE_KEY = "juggernaut_clients";
const ARCHIVED_FILES_STORAGE_KEY = "juggernaut_archived_files";

/**
 * Engagement Service
 * Manages engagement data storage and retrieval
 * Currently uses localStorage, but structured to easily migrate to SharePoint/API
 */
export class EngagementService {
  /**
   * Get all engagements for the current user
   */
  static getAllEngagements(): Engagement[] {
    try {
      const stored = localStorage.getItem(ENGAGEMENTS_STORAGE_KEY);
      if (!stored) {
        // Initialize with the existing Textron engagement as default
        const defaultEngagement: Engagement = {
          id: "textron-india-2023-24",
          clientId: "textron-india",
          clientName: "ABC Private Limited",
          typeOfEngagement: "Financial statement audit",
          financialReportingFramework: "Accounting Standards (AS)",
          reportingDate: new Date("2024-03-31").toISOString(),
          financialYear: "2023-24",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        this.saveEngagements([defaultEngagement]);
        return [defaultEngagement];
      }
      return JSON.parse(stored);
    } catch (error) {
      console.error("Error loading engagements:", error);
      return [];
    }
  }

  /**
   * Get engagement by ID
   */
  static getEngagementById(id: string): Engagement | null {
    const engagements = this.getAllEngagements();
    return engagements.find((e) => e.id === id) || null;
  }

  /**
   * Create a new engagement
   */
  static createEngagement(data: EngagementFormData): Engagement {
    const engagements = this.getAllEngagements();
    
    // Generate engagement ID
    const financialYear = data.reportingDate
      ? this.getFinancialYearFromDate(data.reportingDate)
      : new Date().getFullYear().toString();
    
    const engagementId = `${data.clientId.toLowerCase().replace(/\s+/g, "-")}-${financialYear}`;
    
    const newEngagement: Engagement = {
      id: engagementId,
      clientId: data.clientId,
      clientName: data.clientName,
      engagementName: data.engagementName,
      typeOfEngagement: data.typeOfEngagement,
      financialReportingFramework: data.financialReportingFramework,
      reportingDate: data.reportingDate,
      rollforwardedFromFileId: data.rollforwardedFromFileId,
      financialYear,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    engagements.push(newEngagement);
    this.saveEngagements(engagements);

    // Mark archived file as rollforwarded if applicable
    if (data.rollforwardedFromFileId) {
      this.markFileAsRollforwarded(data.rollforwardedFromFileId, engagementId);
    }

    return newEngagement;
  }

  /**
   * Update an engagement
   */
  static updateEngagement(id: string, data: Partial<EngagementFormData>): Engagement | null {
    const engagements = this.getAllEngagements();
    const index = engagements.findIndex((e) => e.id === id);
    
    if (index === -1) return null;

    engagements[index] = {
      ...engagements[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };

    this.saveEngagements(engagements);
    return engagements[index];
  }

  /**
   * Delete an engagement
   */
  static deleteEngagement(id: string): boolean {
    const engagements = this.getAllEngagements();
    const filtered = engagements.filter((e) => e.id !== id);
    
    if (filtered.length === engagements.length) return false;
    
    this.saveEngagements(filtered);
    return true;
  }

  /**
   * Get all clients
   */
  static getAllClients(): Client[] {
    try {
      const stored = localStorage.getItem(CLIENTS_STORAGE_KEY);
      if (!stored) {
        // Initialize with default client
        const defaultClient: Client = {
          id: "textron-india",
          name: "ABC Private Limited",
          code: "TXT001",
          typeOfCompany: "Private Limited",
          listedUnlisted: "Unlisted",
        };
        this.saveClients([defaultClient]);
        return [defaultClient];
      }
      return JSON.parse(stored);
    } catch (error) {
      console.error("Error loading clients:", error);
      return [];
    }
  }

  /**
   * Search clients by name
   */
  static searchClients(query: string): Client[] {
    const clients = this.getAllClients();
    const lowerQuery = query.toLowerCase();
    return clients.filter(
      (client) =>
        client.name.toLowerCase().includes(lowerQuery) ||
        client.code?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Get client by ID
   */
  static getClientById(id: string): Client | null {
    const clients = this.getAllClients();
    return clients.find((c) => c.id === id) || null;
  }

  /**
   * Create a new client
   */
  static createClient(client: Omit<Client, "id">): Client {
    const clients = this.getAllClients();
    const newClient: Client = {
      ...client,
      id: `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
    clients.push(newClient);
    this.saveClients(clients);
    return newClient;
  }

  /**
   * Get archived files for a client that haven't been rollforwarded
   */
  static getAvailableArchivedFiles(clientId: string): ArchivedFile[] {
    const files = this.getAllArchivedFiles();
    return files.filter(
      (file) => file.clientId === clientId && !file.rollforwarded
    );
  }

  /**
   * Get all archived files
   */
  static getAllArchivedFiles(): ArchivedFile[] {
    try {
      const stored = localStorage.getItem(ARCHIVED_FILES_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Error loading archived files:", error);
      return [];
    }
  }

  /**
   * Mark a file as rollforwarded
   */
  private static markFileAsRollforwarded(fileId: string, engagementId: string): void {
    const files = this.getAllArchivedFiles();
    const file = files.find((f) => f.id === fileId);
    if (file) {
      file.rollforwarded = true;
      file.rollforwardedToEngagementId = engagementId;
      this.saveArchivedFiles(files);
    }
  }

  /**
   * Get financial year from date (e.g., "2024-03-31" -> "2023-24")
   */
  private static getFinancialYearFromDate(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    
    // If date is before April, it's part of previous financial year
    if (month < 4) {
      return `${year - 1}-${year.toString().slice(-2)}`;
    }
    return `${year}-${(year + 1).toString().slice(-2)}`;
  }

  /**
   * Check if client has previous engagements to determine Financial Reporting Framework options
   */
  static getClientPreviousFramework(clientId: string): FinancialReportingFramework | null {
    const engagements = this.getAllEngagements();
    const clientEngagements = engagements
      .filter((e) => e.clientId === clientId)
      .sort((a, b) => 
        (a.createdAt > b.createdAt ? -1 : 1)
      );
    
    if (clientEngagements.length === 0) return null;
    
    return clientEngagements[0].financialReportingFramework || null;
  }

  /**
   * Get available Financial Reporting Framework options based on client history
   */
  static getAvailableFrameworks(clientId: string): {
    isFirstTime: boolean;
    options: FinancialReportingFramework[];
  } {
    const previousFramework = this.getClientPreviousFramework(clientId);
    
    if (!previousFramework) {
      // First time audit
      return {
        isFirstTime: true,
        options: [
          "Accounting Standards (AS)",
          "Indian Accounting Standards (Ind AS)",
          "Transition to Ind AS",
        ],
      };
    }

    if (previousFramework === "Indian Accounting Standards (Ind AS)") {
      // Already on Ind AS, don't ask again
      return {
        isFirstTime: false,
        options: [],
      };
    }

    if (previousFramework === "Transition to Ind AS") {
      // Already transitioned, default to Ind AS
      return {
        isFirstTime: false,
        options: [],
      };
    }

    // Previous was AS, can continue with AS or transition
    return {
      isFirstTime: false,
      options: ["Accounting Standards (AS)", "Transition to Ind AS"],
    };
  }

  // Private helper methods
  private static saveEngagements(engagements: Engagement[]): void {
    localStorage.setItem(ENGAGEMENTS_STORAGE_KEY, JSON.stringify(engagements));
  }

  private static saveClients(clients: Client[]): void {
    localStorage.setItem(CLIENTS_STORAGE_KEY, JSON.stringify(clients));
  }

  private static saveArchivedFiles(files: ArchivedFile[]): void {
    localStorage.setItem(ARCHIVED_FILES_STORAGE_KEY, JSON.stringify(files));
  }
}

